import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  BulkMoveToTrashDocument,
  type BulkMoveToTrashMutation,
  type BulkMoveToTrashMutationVariables,
  GetMediaDocument,
  type GetMediaQuery,
  type GetMediaQueryVariables,
  GetTrashedMediaDocument,
  type MediaType,
  SearchMediaDocument,
  type SearchMediaQuery,
  type SearchMediaQueryVariables,
} from "../../generated/graphql";
import { getMediaConfig } from "./config";
import {
  ConfirmationModal,
  MediaEditModal,
  MoveMediaModal,
  RenameMediaModal,
  useMediaSelection,
} from "./modals";
import { FloatingActionBar } from "./table";
import { EmptyState, MediaTableRow, TableHeader } from "./table";
import type { MediaItem } from "./types";

interface MediaTableViewProps {
  folderId: string | null;
  mediaType: MediaType;
  searchQuery?: string;
}

type SortField = "filename" | "audioText" | "createdAt";
type SortOrder = "asc" | "desc";

export function MediaTableView({ folderId, mediaType, searchQuery = "" }: MediaTableViewProps) {
  const selection = useMediaSelection();
  const isSearching = searchQuery.trim().length > 0;
  const config = getMediaConfig(mediaType);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeItem, setActiveItem] = useState<MediaItem | null>(null);
  const [itemsToAction, setItemsToAction] = useState<MediaItem[]>([]);
  const [itemsToDelete, setItemsToDelete] = useState<MediaItem[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk move to trash mutation
  const [bulkMoveToTrash] = useMutation<BulkMoveToTrashMutation, BulkMoveToTrashMutationVariables>(
    BulkMoveToTrashDocument,
    {
      refetchQueries: [
        {
          query: GetMediaDocument,
          variables: { folderId: folderId ?? undefined, type: mediaType },
        },
        {
          query: GetTrashedMediaDocument,
          variables: { type: mediaType, folderId },
        },
      ],
      onCompleted: (data) => {
        const count = data.bulkMoveToTrash;
        toast.success(`Moved ${count} ${count === 1 ? "item" : "items"} to trash`);
        selection.clearSelection();
      },
      onError: (error) => {
        toast.error(`Failed to move to trash: ${error.message}`);
      },
    }
  );

  // Query media (works for both folder and root level) with pagination
  const {
    data,
    loading: mediaLoading,
    fetchMore,
    networkStatus,
  } = useQuery<GetMediaQuery, GetMediaQueryVariables>(GetMediaDocument, {
    variables: {
      folderId: folderId || undefined,
      type: mediaType,
      first: 20,
    },
    skip: isSearching,
    notifyOnNetworkStatusChange: true,
  });

  // Query search results
  const { data: searchData, loading: searchLoading } = useQuery<
    SearchMediaQuery,
    SearchMediaQueryVariables
  >(SearchMediaDocument, {
    variables: {
      query: searchQuery,
      type: mediaType,
      folderId: folderId || undefined,
    },
    skip: !isSearching,
  });

  // NetworkStatus values: 1=loading, 3=fetchMore, 7=ready
  const isFetchingMore = networkStatus === 3;
  const isInitialLoading = mediaLoading && !isFetchingMore;
  const loading = isSearching ? searchLoading : isInitialLoading;

  const media = isSearching
    ? (searchData?.searchMedia as MediaItem[]) || []
    : (data?.media?.edges?.map((e) => e.node) as MediaItem[]) || [];

  // Infinite scroll for media
  const observerTarget = useRef<HTMLTableRowElement>(null);
  const pageInfo = data?.media?.pageInfo;
  const hasMore = pageInfo?.hasNextPage || false;

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (!target) return;
      if (target.isIntersecting && hasMore && !isFetchingMore && !isSearching) {
        fetchMore({
          variables: {
            after: pageInfo?.endCursor,
          },
        });
      }
    },
    [hasMore, isFetchingMore, isSearching, fetchMore, pageInfo?.endCursor]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element || isSearching) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver, isSearching]);

  const sortedMedia = [...media].sort((a, b) => {
    let comparison = 0;
    if (sortField === "filename") {
      comparison = a.filename.localeCompare(b.filename);
    } else if (sortField === "audioText") {
      const aText = ("audioText" in a ? a.audioText : null) || "";
      const bText = ("audioText" in b ? b.audioText : null) || "";
      comparison = aText.localeCompare(bText);
    } else {
      comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleSelectAll = () => {
    if (selection.selectedItems.length === sortedMedia.length && sortedMedia.length > 0) {
      selection.clearSelection();
    } else {
      selection.selectAll(sortedMedia);
    }
  };

  const handleEdit = (item: MediaItem) => {
    setActiveItem(item);
    setShowEditModal(true);
  };

  const handleMove = (item: MediaItem) => {
    setItemsToAction([item]);
    setShowMoveModal(true);
  };

  const handleDelete = (item: MediaItem) => {
    setItemsToDelete([item]);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (itemsToDelete.length === 0) return;

    setIsDeleting(true);
    try {
      const ids = itemsToDelete.map((item) => item.id);
      await bulkMoveToTrash({ variables: { ids } });
    } catch (_error) {
      // Error already handled by mutation onError
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setItemsToDelete([]);
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setShowRenameModal(false);
    setShowMoveModal(false);
    setActiveItem(null);
    setItemsToAction([]);
    selection.clearSelection();
  };

  const handleBulkRename = (item: MediaItem) => {
    setActiveItem(item);
    setShowRenameModal(true);
  };

  const handleBulkMove = (items: MediaItem[]) => {
    setItemsToAction(items);
    setShowMoveModal(true);
  };

  const handleBulkDelete = (items: MediaItem[]) => {
    setItemsToDelete(items);
    setShowDeleteConfirm(true);
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-12 px-4 py-3" />
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                File
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Audio Text
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Tags
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                URL
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map((skeletonId) => (
              <tr key={skeletonId}>
                <td className="px-4 py-3" colSpan={6}>
                  <div className="h-12 animate-pulse rounded bg-gray-200" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (sortedMedia.length === 0) {
    return <EmptyState mediaType={mediaType} />;
  }

  const allSelected =
    selection.selectedItems.length === sortedMedia.length && sortedMedia.length > 0;
  const someSelected =
    selection.selectedItems.length > 0 && selection.selectedItems.length < sortedMedia.length;

  return (
    <>
      <div
        className="rounded-lg border border-gray-200 bg-white"
        style={{ overflowAnchor: "none" }}
      >
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <TableHeader
            columns={config.tableColumns}
            sortField={sortField}
            sortOrder={sortOrder}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
          />
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedMedia.map((item) => (
              <MediaTableRow
                key={item.id}
                media={item}
                isSelected={selection.isSelected(item.id)}
                columns={config.tableColumns}
                onToggleSelect={() => selection.toggleSelection(item)}
                onEdit={() => handleEdit(item)}
                onMove={() => handleMove(item)}
                onDelete={() => handleDelete(item)}
              />
            ))}
            {/* Infinite scroll trigger */}
            {hasMore && !isSearching && (
              <tr ref={observerTarget}>
                <td colSpan={6} className="py-4 text-center">
                  {isFetchingMore && (
                    <div className="flex items-center justify-center">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                      <span className="ml-2 text-sm text-gray-500">Loading more...</span>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Floating Action Bar for Bulk Selection */}
      {selection.selectedItems.length > 0 && (
        <FloatingActionBar
          selectedItems={selection.selectedItems}
          selectedCount={selection.selectedItems.length}
          onClearSelection={selection.clearSelection}
          onRename={handleBulkRename}
          onMove={handleBulkMove}
          onDelete={handleBulkDelete}
        />
      )}

      {/* Modals */}
      {showEditModal && activeItem && (
        <MediaEditModal isOpen={showEditModal} onClose={handleModalClose} media={activeItem} />
      )}
      {showRenameModal && activeItem && (
        <RenameMediaModal
          isOpen={showRenameModal}
          onClose={handleModalClose}
          mediaId={activeItem.id}
          currentFilename={activeItem.filename}
        />
      )}
      {showMoveModal && itemsToAction.length > 0 && itemsToAction[0] && (
        <MoveMediaModal
          isOpen={showMoveModal}
          onClose={handleModalClose}
          mediaIds={itemsToAction.map((item) => item.id)}
          mediaType={itemsToAction[0].type}
          currentFolderId={itemsToAction[0].folderId ?? null}
        />
      )}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemsToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        title="Move to Trash"
        message={
          itemsToDelete.length === 1
            ? `Move "${itemsToDelete[0]?.filename}" to trash?`
            : `Move ${itemsToDelete.length} items to trash?`
        }
        confirmLabel="Move to Trash"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
}
